import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { Compass, Loader2, Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
        nav("/");
      } else {
        const { data } = await api.post("/auth/forgot-password", { email });
        toast.success("If the email exists, a reset link has been sent.");
        if (data?.reset_link) console.log("Reset link:", data.reset_link);
        setMode("login");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const fillSample = (e, p) => { setEmail(e); setPassword(p); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Right visual — placed left on lg */}
      <div className="hidden lg:flex relative star-field text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1120]/60 via-transparent to-[#0B1120]/90" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-slate-900 shadow-lg">
              <Compass className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-display text-xl">Trinandan Mishra</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80">Vedic Wisdom · Modern CRM</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-light leading-tight tracking-tight">
              Guide destinies.<br />
              <span className="text-amber-300">Grow your practice.</span>
            </h1>
            <p className="mt-6 max-w-md text-slate-300 leading-relaxed">
              A premium command center for astrology, Vastu, numerology & gemstone consultations —
              built for the wisdom-keepers of tomorrow.
            </p>
            <div className="mt-10 gold-divider max-w-xs" />
            <div className="mt-6 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              Trusted by 10,000+ Devotees Worldwide
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white dark:bg-slate-950">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-slate-900">
              <Compass className="w-5 h-5" />
            </div>
            <div className="font-display text-xl">Trinandan CRM</div>
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs font-medium border border-amber-200 dark:border-amber-500/30">
              <Sparkles className="w-3 h-3" strokeWidth={2} /> Enterprise Astrology CRM
            </div>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-light tracking-tight text-slate-900 dark:text-white">
              {mode === "login" ? "Sign in to your workspace" : "Reset your password"}
            </h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {mode === "login" ? "Enter your credentials to continue." : "Enter your email; we'll send a secure reset link."}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl mt-1" data-testid="login-email" placeholder="you@trinandanmishra.com" />
            </div>
            {mode === "login" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Password</Label>
                  <button type="button" onClick={() => setMode("forgot")} className="text-xs text-amber-600 hover:text-amber-700" data-testid="forgot-password-link">Forgot?</button>
                </div>
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl mt-1" data-testid="login-password" placeholder="••••••••" />
              </div>
            )}
          </div>

          <Button type="submit" disabled={busy}
            className="w-full h-12 mt-6 rounded-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-medium tracking-wide btn-pill"
            data-testid="login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign in" : "Send reset link"}
          </Button>

          {mode === "forgot" && (
            <button type="button" onClick={() => setMode("login")} className="w-full mt-3 text-sm text-slate-500 hover:text-slate-700">Back to sign in</button>
          )}

          {mode === "login" && (
            <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Demo Access</div>
              <div className="grid grid-cols-1 gap-2">
                {[
                  ["Super Admin", "admin@trinandanmishra.com", "admin@123"],
                  ["Astrologer", "astro@trinandanmishra.com", "password123"],
                  ["Marketing", "marketing@trinandanmishra.com", "password123"],
                ].map(([label, em, pw]) => (
                  <button key={em} type="button" onClick={() => fillSample(em, pw)}
                    className="text-left px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-500/5 text-xs transition-colors"
                    data-testid={`demo-${label.toLowerCase().replace(' ','-')}`}>
                    <span className="font-medium text-slate-700 dark:text-slate-200">{label}</span>
                    <span className="text-slate-500 ml-2">{em}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
